import React from 'react';
import {
	Box,
	FormControlLabel,
	Grid,
	Radio,
	RadioGroup,
	Tooltip,
	Typography,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { Component as ComponentType } from '@Types';
import { useGlobalization } from '@Translations/useGlobalization';
import { palette } from '@innovyze/stylovyze';
import { CATEGORY_ACCENT } from './categoryColors';

interface CategoryComponentsHeaderProps {
	components?: ComponentType[];
	useHighestScore?: boolean;
	categoryWeighting?: number;
	isLoading: boolean;
	onBalance: () => void;
	onUseHighestChange: (checked: boolean) => void;
}

export const CategoryComponentsHeader = ({
	components,
	useHighestScore,
	categoryWeighting,
	isLoading,
	onBalance,
	onUseHighestChange,
}: CategoryComponentsHeaderProps) => {
	const { t } = useGlobalization();

	// Sum of the components that are switched on — they must reach 100%
	// within the category, just like category weights do across the model.
	const compTotal = (components ?? [])
		.filter(c => c.select)
		.reduce((s, c) => s + (c.weighting || 0), 0);
	const compRounded = Math.round(compTotal);
	const compBalanced = compRounded === 100;
	const compDiff = compRounded - 100;

	let balanceMessage: string;
	if (compBalanced) {
		balanceMessage = t('Components total 100%');
	} else if (compDiff > 0) {
		balanceMessage = t(
			'{{over}}% over — reduce component weights to reach 100%',
			{ over: compDiff },
		);
	} else {
		balanceMessage = t(
			'{{left}}% left — component weights must total 100%',
			{ left: -compDiff },
		);
	}

	return (
		<Grid
			container
			alignItems="center"
			justifyContent="space-between"
			style={{ marginTop: '8px', marginBottom: '4px' }}>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'baseline',
					gap: 1.5,
					flexWrap: 'wrap',
				}}>
				<Typography
					sx={{
						fontSize: 11,
						fontWeight: 700,
						letterSpacing: '0.06em',
						textTransform: 'uppercase',
						color: '#97a0ac',
					}}>
					{t('Components')}
				</Typography>
				{!isLoading &&
					!useHighestScore &&
					(components?.length ?? 0) > 0 && (
						<>
							<Box
								component="span"
								sx={{
									px: 1,
									py: '2px',
									borderRadius: '9999px',
									fontSize: 12.5,
									fontWeight: 700,
									color: compBalanced ? '#1b7a4b' : '#c0392b',
									background: compBalanced
										? '#e6f4ea'
										: '#fdecea',
								}}>
								{compRounded}%
							</Box>
							<Typography
								sx={{
									fontSize: 12.5,
									color: compBalanced
										? 'text.secondary'
										: '#c0392b',
								}}>
								{balanceMessage}
							</Typography>
							{!compBalanced && compRounded > 0 && (
								<Box
									component="button"
									onClick={onBalance}
									sx={{
										border: 'none',
										background: 'transparent',
										cursor: 'pointer',
										font: 'inherit',
										fontSize: 12.5,
										fontWeight: 700,
										color: palette.primary.main,
										p: 0,
										textDecoration: 'underline',
										textUnderlineOffset: '2px',
										'&:hover': {
											color: palette.primary.dark,
										},
									}}>
									{t('Balance to 100%')}
								</Box>
							)}
						</>
					)}
			</Box>
			{!isLoading && (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 0.5,
						}}>
						<Typography
							sx={{ fontSize: 13, color: 'text.secondary' }}>
							{t('Combine scores by')}
						</Typography>
						<Tooltip
							arrow
							title={t(
								'How a category turns its components into one score. Weighted average blends them by their weights. Highest score takes the single worst (highest-risk) component and ignores the weights — use it when one severe factor should drive the result.',
							)}>
							<InfoOutlined
								sx={{
									fontSize: 15,
									color: '#b3bac4',
									cursor: 'default',
								}}
							/>
						</Tooltip>
					</Box>
					<RadioGroup
						row
						aria-label={t('Combine scores by')}
						value={useHighestScore ? 'highest' : 'weighted'}
						onChange={(_, v) => onUseHighestChange(v === 'highest')}
						sx={{
							gap: 2,
							opacity: categoryWeighting === 0 ? 0.5 : 1,
						}}>
						{[
							{ v: 'weighted', label: t('Weighted average') },
							{ v: 'highest', label: t('Highest score') },
						].map(opt => (
							<FormControlLabel
								key={opt.v}
								value={opt.v}
								disabled={categoryWeighting === 0}
								sx={{ m: 0 }}
								control={
									<Radio
										size="small"
										sx={{
											p: 0.5,
											color: '#c2c9d2',
											'&.Mui-checked': {
												color: CATEGORY_ACCENT,
											},
										}}
									/>
								}
								label={
									<Typography
										sx={{
											fontSize: 12.5,
											fontWeight: 600,
											color: '#1b2733',
										}}>
										{opt.label}
									</Typography>
								}
							/>
						))}
					</RadioGroup>
				</Box>
			)}
		</Grid>
	);
};
